import type {
    AiTradeSession,
    DraftKey,
    NeutralTradeSession,
    PatternAiAction,
    PatternPageAiState,
    SignalTradeDraft,
} from '../types';
import {createTradeDrafts} from '../utils/tradeDrafts';

const INITIAL_CAPABILITIES = {
    overlayEnabled: true,
    quickTradeEnabled: true,
    priceDeviationThreshold: 0,
    maxCandleAge: 0,
};

export function createInitialAiState(): PatternPageAiState {
    return {
        capabilities: INITIAL_CAPABILITIES,
        capabilitiesStatus: 'idle',
        analysisByKey: {},
        analysisRequestByKey: {},
        expandedAnalysisKey: null,
        selectedSignalKey: null,
        overlayVisible: false,
        tradeSession: null,
        tradePanelOpen: false,
        submissionSnapshot: null,
        submissionStatus: 'idle',
        submissionMessage: null,
        submissionPollingDeadline: null,
    };
}

function buildDirectionalSession(
    analysisKey: string,
    aiAnalysisId: number,
    analysis: {
        entryPrice: number | null; targetPrice: number | null; stopLossPrice: number | null;
        signalDirection: 1 | -1; signalConfidence?: number
    },
): AiTradeSession {
    const drafts = createTradeDrafts(
        analysis.signalDirection,
        validConfidence(analysis.signalConfidence),
        draftPrice(analysis.entryPrice),
        draftPrice(analysis.targetPrice),
        draftPrice(analysis.stopLossPrice),
    );
    return {
        analysisKey: analysisKey as AiTradeSession['analysisKey'],
        aiAnalysisId,
        selectedDraftKey: 'FORWARD',
        forwardDraft: drafts.FORWARD!,
        reverseDraft: drafts.REVERSE!,
    };
}

function buildNeutralSession(
    analysisKey: string,
    aiAnalysisId: number,
    analysis: {
        entryPrice: number | null; targetPrice: number | null; stopLossPrice: number | null;
        signalConfidence?: number
    },
): NeutralTradeSession {
    const drafts = createTradeDrafts(
        0,
        validConfidence(analysis.signalConfidence),
        draftPrice(analysis.entryPrice),
        draftPrice(analysis.targetPrice),
        draftPrice(analysis.stopLossPrice),
    );
    return {
        analysisKey: analysisKey as NeutralTradeSession['analysisKey'],
        aiAnalysisId,
        selectedDraftKey: null,
        manualLongDraft: drafts.MANUAL_LONG!,
        manualShortDraft: drafts.MANUAL_SHORT!,
    };
}

function draftPrice(value: number | null): number {
    return null == value || !Number.isFinite(value) ? 0 : value;
}

function validConfidence(value?: number): number | undefined {
    return null != value && Number.isFinite(value) && 0 <= value && 100 >= value ? value : undefined;
}

function getDraftByKey(
    session: (AiTradeSession | NeutralTradeSession) | null,
    draftKey: DraftKey | null,
): SignalTradeDraft | null {
    if (null == session || null == draftKey) return null;
    if ('FORWARD' === draftKey) return (session as AiTradeSession).forwardDraft ?? null;
    if ('REVERSE' === draftKey) return (session as AiTradeSession).reverseDraft ?? null;
    if ('MANUAL_LONG' === draftKey) return (session as NeutralTradeSession).manualLongDraft ?? null;
    if ('MANUAL_SHORT' === draftKey) return (session as NeutralTradeSession).manualShortDraft ?? null;
    return null;
}

export function patternAiReducer(
    state: PatternPageAiState,
    action: PatternAiAction,
): PatternPageAiState {
    switch (action.type) {

        case 'CAPABILITIES_REQUEST_STARTED':
            return {...state, capabilitiesStatus: 'loading'};

        case 'CAPABILITIES_REQUEST_SUCCEEDED':
            return {
                ...state,
                capabilities: action.payload,
                capabilitiesStatus: 'succeeded',
            };

        case 'CAPABILITIES_REQUEST_FAILED': {
            const keepSubmission = isSubmissionPending(state.submissionStatus);
            return {
                ...state,
                capabilities: {
                    overlayEnabled: false,
                    quickTradeEnabled: false,
                    priceDeviationThreshold: 0,
                    maxCandleAge: 0,
                },
                capabilitiesStatus: 'failed',
                overlayVisible: false,
                tradeSession: keepSubmission ? state.tradeSession : null,
                tradePanelOpen: false,
            };
        }

        case 'AI_REQUEST_STARTED': {
            if (isSubmissionPending(state.submissionStatus)) return state;
            const prev = state.analysisRequestByKey[action.key];
            return {
                ...state,
                analysisRequestByKey: {
                    ...state.analysisRequestByKey,
                    [action.key]: {
                        status: 'loading',
                        requestSeq: action.requestSeq,
                        error: undefined,
                    },
                },
            };
        }

        case 'AI_REQUEST_SUCCEEDED': {
            const prev = state.analysisRequestByKey[action.key];
            if (prev?.requestSeq !== action.requestSeq) return state;
            const analysis = action.analysis;

            let tradeSession = state.tradeSession;
            let tradePanelOpen = state.tradePanelOpen;
            if (tradeSession?.analysisKey === action.key
                && tradeSession.aiAnalysisId !== analysis.id) {
                tradeSession = null;
                tradePanelOpen = false;
            }

            return {
                ...state,
                analysisByKey: {
                    ...state.analysisByKey,
                    [action.key]: analysis,
                },
                analysisRequestByKey: {
                    ...state.analysisRequestByKey,
                    [action.key]: {status: 'succeeded', requestSeq: action.requestSeq},
                },
                overlayVisible: state.expandedAnalysisKey === action.key
                    ? true : state.overlayVisible,
                tradeSession,
                tradePanelOpen,
            };
        }

        case 'AI_REQUEST_FAILED': {
            const prev = state.analysisRequestByKey[action.key];
            if (prev?.requestSeq !== action.requestSeq) return state;
            return {
                ...state,
                analysisRequestByKey: {
                    ...state.analysisRequestByKey,
                    [action.key]: {
                        status: 'failed',
                        requestSeq: prev?.requestSeq ?? 0,
                        error: action.error,
                    },
                },
            };
        }

        case 'EXPAND_ANALYSIS': {
            if (isSubmissionPending(state.submissionStatus)) return state;
            const analysis = state.analysisByKey[action.key];
            return {
                ...state,
                expandedAnalysisKey: action.key,
                selectedSignalKey: action.key,
                overlayVisible: !!analysis,
                tradeSession: null,
                tradePanelOpen: false,
                submissionSnapshot: null,
                submissionStatus: 'idle',
                submissionMessage: null,
                submissionPollingDeadline: null,
            };
        }

        case 'COLLAPSE_ANALYSIS': {
            const keepSubmission = isSubmissionPending(state.submissionStatus);
            return {
                ...state,
                expandedAnalysisKey: null,
                overlayVisible: false,
                tradeSession: keepSubmission ? state.tradeSession : null,
                tradePanelOpen: keepSubmission ? state.tradePanelOpen : false,
                submissionSnapshot: keepSubmission ? state.submissionSnapshot : null,
                submissionStatus: keepSubmission ? state.submissionStatus : 'idle',
                submissionMessage: keepSubmission ? state.submissionMessage : null,
                submissionPollingDeadline: keepSubmission ? state.submissionPollingDeadline : null,
            };
        }

        case 'SET_OVERLAY_VISIBLE':
            return {...state, overlayVisible: action.visible};

        case 'OPEN_TRADE_SESSION': {
            if (isSubmissionPending(state.submissionStatus)) {
                if (state.submissionSnapshot?.analysisKey !== action.key || null == state.tradeSession) return state;
                return {...state, tradePanelOpen: true};
            }
            const analysis = state.analysisByKey[action.key];
            if (null == analysis) return state;

            const signalDirection = analysis.signalDirection;
            const session = 0 === signalDirection
                ? buildNeutralSession(action.key, analysis.id, analysis)
                : buildDirectionalSession(action.key, analysis.id, {
                    ...analysis,
                    signalDirection,
                });

            return {
                ...state,
                expandedAnalysisKey: action.key,
                tradeSession: session,
                tradePanelOpen: true,
                overlayVisible: true,
                submissionSnapshot: state.submissionSnapshot,
                submissionStatus: state.submissionStatus,
                submissionMessage: state.submissionMessage,
            };
        }

        case 'SELECT_TRADE_INTENT': {
            if (null == state.tradeSession) return state;
            return {
                ...state,
                tradeSession: {...state.tradeSession, selectedDraftKey: action.draftKey},
            };
        }

        case 'UPDATE_DRAFT': {
            if (null == state.tradeSession) return state;
            const session = state.tradeSession;
            if ('FORWARD' === action.draftKey && 'forwardDraft' in session) {
                return {
                    ...state,
                    tradeSession: {
                        ...session,
                        forwardDraft: {...session.forwardDraft, ...action.updates, manuallyEdited: true},
                    },
                };
            }
            if ('REVERSE' === action.draftKey && 'reverseDraft' in session) {
                return {
                    ...state,
                    tradeSession: {
                        ...session,
                        reverseDraft: {...session.reverseDraft, ...action.updates, manuallyEdited: true},
                    },
                };
            }
            if ('MANUAL_LONG' === action.draftKey && 'manualLongDraft' in session) {
                const ns = session as NeutralTradeSession;
                return {
                    ...state,
                    tradeSession: {
                        ...ns,
                        manualLongDraft: {...ns.manualLongDraft, ...action.updates, manuallyEdited: true},
                    },
                };
            }
            if ('MANUAL_SHORT' === action.draftKey && 'manualShortDraft' in session) {
                const ns = session as NeutralTradeSession;
                return {
                    ...state,
                    tradeSession: {
                        ...ns,
                        manualShortDraft: {...ns.manualShortDraft, ...action.updates, manuallyEdited: true},
                    },
                };
            }
            return state;
        }

        case 'CLOSE_TRADE_SESSION': {
            const submission = state.submissionSnapshot;
            const subStatus = state.submissionStatus;
            const keepSnapshot = 'submitting' === subStatus
                || 'unknown' === subStatus || 'processing' === subStatus
                || 'under_review' === subStatus;
            return {
                ...state,
                tradeSession: keepSnapshot ? state.tradeSession : null,
                tradePanelOpen: false,
                submissionSnapshot: keepSnapshot ? submission : null,
                submissionStatus: keepSnapshot ? subStatus : 'idle',
                submissionMessage: keepSnapshot ? state.submissionMessage : null,
                submissionPollingDeadline: keepSnapshot ? state.submissionPollingDeadline : null,
            };
        }

        case 'REOPEN_TRADE_SESSION':
            return null == state.tradeSession && null == state.submissionSnapshot
                ? state : {...state, tradePanelOpen: true};

        case 'FREEZE_SUBMISSION':
            return {
                ...state,
                submissionSnapshot: action.snapshot,
                submissionStatus: 'submitting',
                submissionMessage: null,
                submissionPollingDeadline: action.pollingDeadline ?? state.submissionPollingDeadline,
            };

        case 'SET_SUBMISSION_STATUS':
            return {
                ...state,
                submissionStatus: action.status,
                submissionMessage: undefined === action.message
                    ? state.submissionMessage : action.message,
                submissionPollingDeadline: 'succeeded' === action.status || 'failed' === action.status
                    ? null : state.submissionPollingDeadline,
            };

        case 'CLEAR_SUBMISSION':
            return {
                ...state, submissionSnapshot: null, submissionStatus: 'idle', submissionMessage: null,
                submissionPollingDeadline: null
            };

        case 'RESET_CONTEXT':
            const keepSubmission = isSubmissionPending(state.submissionStatus);
            return {
                ...state,
                analysisByKey: {},
                analysisRequestByKey: {},
                expandedAnalysisKey: null,
                selectedSignalKey: null,
                overlayVisible: false,
                tradeSession: null,
                tradePanelOpen: false,
                submissionSnapshot: keepSubmission ? state.submissionSnapshot : null,
                submissionStatus: keepSubmission ? state.submissionStatus : 'idle',
                submissionMessage: keepSubmission ? state.submissionMessage : null,
                submissionPollingDeadline: keepSubmission ? state.submissionPollingDeadline : null,
            };

        default:
            return state;
    }
}

function isSubmissionPending(status: PatternPageAiState['submissionStatus']): boolean {
    return 'submitting' === status || 'processing' === status
        || 'unknown' === status || 'under_review' === status;
}
